import type { SupabaseClient } from '@supabase/supabase-js'
import { fetchCompetitors } from '~/composables/useCompetitors'
import { fetchAgentTasks } from '~/composables/useAgentTasks'
import { fetchUserAlerts } from '~/composables/useUserAlerts'

export default defineNuxtRouteMiddleware(async (to) => {
  // Only apply to routes that start with /company/
  // Skip on server-side to prevent hydration mismatches
  if (import.meta.server) return

  const { 
    subscribeToCompetitorMonitoring,
    subscribeToAgentTasks,
    subscribeToUserAlerts, 
    subscribeToCompany 
  } = useRealtimeListeners()

  console.log('MIDDLEWARE: Route:', to)

  if (!to.path.startsWith('/company/')) {
    console.log('MIDDLEWARE: Not a company route')
    return
  }

  // User is accessing a company page
  const company = useCompanyData()

  // This is the public_id of the company used in the url
  const company_public_id = to.params.company_public_id


  console.log('MIDDLEWARE: Company public ID:', company_public_id)
  console.log('MIDDLEWARE: Current company data public_id:', company.value?.public_id)
  
  // If we already have matching company data, continue
  if (company.value?.public_id.toString() === company_public_id.toString()) {
    console.log('MIDDLEWARE: Company already exists, skipping fetch')
    return
  }
  
  console.log(`${company_public_id} !== ${company.value?.public_id}`)

  // we need to fetch the company data from the database
  try {
    const { $supabase } = useNuxtApp()
    const supabase = $supabase as SupabaseClient
    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token
    
    if (!accessToken) {
      throw new Error('No access token available')
    }

    const response: { data: { id: string } } = await $fetch(`/api/company?company_id=${company_public_id}`, {
      headers: {
        Authorization: accessToken
      }
    })

    console.log('MIDDLEWARE: Company response:', response)
    
    company.value = response.data

    const internal_company_id = response.data.id
    
    // fetch competitors
    fetchCompetitors(internal_company_id)

    // fetch agent tasks
    fetchAgentTasks(internal_company_id)

    // fetch user alerts
    fetchUserAlerts(internal_company_id)

    // start the realtime listeners
    subscribeToCompetitorMonitoring(internal_company_id)
    subscribeToAgentTasks(internal_company_id)
    subscribeToUserAlerts(internal_company_id)
    subscribeToCompany(internal_company_id)
    
  } catch (error: any) {
    console.error('Company fetch error:', error)
    
    // Handle different error types appropriately
    if (error.status === 401) {
      // User is not authenticated - redirect to login
      return navigateTo('/login')
    } else if (error.status === 403) {
      // User is authenticated but doesn't have permission to access this company
      // Force full page reload to avoid hydration mismatches
      if (process.client) {
        window.location.href = '/unauthorized'
        return
      }
    } else {
      // Other server errors
      return navigateTo('/error')
    }
  }
})